// Turn a file the user picked (Guitar Pro, MusicXML or MIDI) into a Score.
// Shared by the editor's Import command, the home page's import button and
// the converter pages. The parsers (core, and alphaTab for Guitar Pro) are
// loaded on demand, so importing this module keeps a page small.

import type { Score } from '@simplenotation/core';

export const GP_EXT = /\.(gp|gp3|gp4|gp5|gpx)$/i;
export const XML_EXT = /\.(musicxml|xml|mxl)$/i;
export const MIDI_EXT = /\.(mid|midi)$/i;

/** The file picker's accept list: every extension the parsers understand. */
export const ACCEPT_ALL = '.musicxml,.xml,.mxl,.gp,.gp3,.gp4,.gp5,.gpx,.mid,.midi';

export type SourceKind = 'guitar-pro' | 'musicxml' | 'midi';

/** Which parser a file name (or, failing that, its first bytes) calls for. */
export function sourceKind(name: string, bytes?: Uint8Array): SourceKind | null {
  if (GP_EXT.test(name)) return 'guitar-pro';
  if (XML_EXT.test(name)) return 'musicxml';
  if (MIDI_EXT.test(name)) return 'midi';
  if (!bytes) return null;
  const head = new TextDecoder().decode(bytes.slice(0, 64));
  if (head.startsWith('MThd')) return 'midi';
  if (head.includes('<?xml') || head.includes('<score-')) return 'musicxml';
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) return 'musicxml';
  return null;
}

export interface ParseResult {
  score: Score;
  /** Non-fatal problems the importer found (things it changed or left out). */
  warnings: string[];
}

/** Parse file bytes by their name (or content) into a score. Throws on unknown or unreadable files. */
export async function parseBytesWithWarnings(name: string, bytes: Uint8Array): Promise<ParseResult> {
  const kind = sourceKind(name, bytes);
  if (kind === 'guitar-pro') {
    const { importGuitarProWithWarnings } = await import('./gp');
    return importGuitarProWithWarnings(bytes);
  }
  const core = await import('@simplenotation/core');
  if (kind === 'musicxml') {
    const compressed = /\.mxl$/i.test(name) || (!XML_EXT.test(name) && bytes[0] === 0x50 && bytes[1] === 0x4b);
    return core.importMusicXmlWithWarnings(compressed ? bytes : new TextDecoder().decode(bytes));
  }
  if (kind === 'midi') return core.importMidiWithWarnings(bytes);
  throw new Error('Unsupported file type. Use MusicXML (.musicxml, .xml, .mxl), Guitar Pro (.gp, .gp3–5, .gpx) or MIDI.');
}

export async function parseBytes(name: string, bytes: Uint8Array): Promise<Score> {
  return (await parseBytesWithWarnings(name, bytes)).score;
}

export async function parseFile(file: File): Promise<Score> {
  return parseBytes(file.name, new Uint8Array(await file.arrayBuffer()));
}
